import { useEffect, useRef, useState } from 'react';

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export function money(n: number): string {
  return `$${n.toFixed(2)}`;
}

/** Reveal an element once it scrolls into view (adds `.in`).
   Robust: reveals immediately if already on screen at mount, observes on
   scroll otherwise, and always falls back to visible after a timeout so
   content can never get stuck hidden if IntersectionObserver misbehaves. */
export function useReveal<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return;

    // Already visible at mount (above the fold, or restored scroll)?
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) {
      setShown(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -6% 0px' },
    );
    io.observe(el);

    // Safety net — never leave content invisible.
    const t = window.setTimeout(() => setShown(true), 2500);
    return () => { io.disconnect(); window.clearTimeout(t); };
  }, [shown]);
  return { ref, shown };
}
